import { getRouterSelectors, type RouterReducerState } from '@ngrx/router-store';
import { createFeatureSelector, createSelector, select, type MemoizedSelector } from '@ngrx/store';
import { filter, first, map, pipe, type Observable, type UnaryFunction } from 'rxjs';

import { OWCC_QUERY_PARAM } from 'src/app/core/config/query-params.config';
import { AppRouteQueryParam } from 'src/app/routing/app-route-query-params';

export const selectNextRouter = createFeatureSelector<RouterReducerState>('nextRouter');

// `router` is used as the default feature name. You can use the feature name
// of your choice by creating a feature selector and pass it to the `getRouterSelectors` function
// export const selectRouter = createFeatureSelector<RouterReducerState>('yourFeatureName');
export const {
  selectCurrentRoute, // select the current route
  selectFragment, // select the current route fragment
  selectQueryParams, // select the current route query params
  selectQueryParam, // factory function to select a query param
  selectRouteParams, // select the current route params
  selectRouteParam, // factory function to select a route param
  selectRouteData, // select the current route data
  selectRouteDataParam, // factory function to select a route data param
  selectUrl, // select the current url
  selectTitle // select the title if available
} = getRouterSelectors(selectNextRouter);

function stringifyQueryParams(params?: string | string[]): string | undefined {
  return params ? (Array.isArray(params) ? params.join(',') : params) : undefined;
}

/**
 * At the very beginning of the app startup the route can be undefined.
 * Selector to be used in combination with other selectors to check if other selectors result are meaningful.
 */
const selectIsValidRoute = createSelector(selectCurrentRoute, currentRoute => currentRoute !== undefined);

/**
 * @returns (open) stream of true, false _or undefined_ (if there is no valid route yet)!
 */
export const selectIsOnSummaryPage = createSelector(
  selectIsValidRoute,
  selectRouteDataParam('isSummary'),
  (isValidRoute, isSummary): boolean | undefined => {
    if (isValidRoute) {
      return isSummary !== undefined;
    }
    return undefined;
  }
);

/**
 * @returns (open) stream of true, false _or undefined_ (if there is no valid route yet)!
 */
export const selectIsOnOnlineCodePage = createSelector(
  selectIsValidRoute,
  selectRouteParam('onlinecode'),
  (isValidRoute, onlineCode): boolean | undefined => {
    if (isValidRoute) {
      return onlineCode !== undefined;
    }
    return undefined;
  }
);

/**
 * Selector to check wheter user enters summary page with a certain parameter.
 *
 * @returns (closed stream of) false or parameter value as string (if param available and on summary page)
 *
 * use with store.pipe()
 */
export const selectEnteringOnSummaryPageWithQueryParam = (
  queryParam: string
): UnaryFunction<Observable<object>, Observable<string | false>> => {
  interface InnerSelectorType {
    isOnSummaryPage: boolean | undefined;
    queryParamValue: string | undefined;
  }

  const selectCombination = (
    param: string
  ): MemoizedSelector<
    object,
    InnerSelectorType,
    (s1: boolean | undefined, s2: string | undefined) => InnerSelectorType
  > =>
    createSelector(selectIsOnSummaryPage, selectQueryParam(param), (isOnSummaryPage, queryParamValue) => ({
      isOnSummaryPage,
      queryParamValue: stringifyQueryParams(queryParamValue)
    }));

  return pipe(
    select(selectCombination(queryParam)),
    filter(({ isOnSummaryPage }) => isOnSummaryPage !== undefined), // we need to wait until it's defined (either true or false)
    first(),
    map(({ isOnSummaryPage, queryParamValue }) =>
      isOnSummaryPage === false || queryParamValue === undefined ? false : queryParamValue
    )
  );
};

const selectEnteringOnOnlineCodePageWithQueryParam = (
  queryParam: string
): UnaryFunction<Observable<object>, Observable<string | false>> => {
  interface InnerSelectorType {
    isOnOnlineCodePage: boolean | undefined;
    queryParamValue: string | undefined;
  }

  const selectCombination = (
    param: string
  ): MemoizedSelector<
    object,
    InnerSelectorType,
    (s1: boolean | undefined, s2: string | undefined) => InnerSelectorType
  > =>
    createSelector(selectIsOnOnlineCodePage, selectQueryParam(param), (isOnOnlineCodePage, queryParamValue) => ({
      isOnOnlineCodePage,
      queryParamValue: stringifyQueryParams(queryParamValue)
    }));

  return pipe(
    select(selectCombination(queryParam)),
    filter(({ isOnOnlineCodePage }) => isOnOnlineCodePage !== undefined), // we need to wait until it's defined (either true or false)
    first(),
    map(({ isOnOnlineCodePage, queryParamValue }) =>
      isOnOnlineCodePage === false || queryParamValue === undefined ? false : queryParamValue
    )
  );
};

/**
 * @returns first value of a query parameter (if defined, undefined otherwise).
 * Only returns result if route is already defined!
 */
const selectQueryParamOnce = (
  queryParam: string
): UnaryFunction<Observable<object>, Observable<string | undefined>> => {
  interface InnerSelectorType {
    isValidRoute: boolean;
    queryParamValue: string | undefined;
  }

  const selectCombination = (
    param: string
  ): MemoizedSelector<object, InnerSelectorType, (s1: boolean, s2: string | undefined) => InnerSelectorType> =>
    createSelector(selectIsValidRoute, selectQueryParam(param), (isValidRoute, queryParamValue) => ({
      isValidRoute,
      queryParamValue: stringifyQueryParams(queryParamValue)
    }));

  return pipe(
    select(selectCombination(queryParam)),
    filter(({ isValidRoute }) => !!isValidRoute), // only proceed is route is defined
    first(),
    map(({ queryParamValue }) => queryParamValue)
  );
};

const selectIsOnUrl = (url: string): MemoizedSelector<object, boolean, (s1: boolean, s2: string) => boolean> =>
  createSelector(selectIsValidRoute, selectUrl, (isValidRoute, selectedUrl) => isValidRoute && selectedUrl === url);

export const b2oSelectors = {
  selectEnteringOnSummaryPageWithDirectSalesParameter: selectEnteringOnSummaryPageWithQueryParam(
    OWCC_QUERY_PARAM.DIRECT_SALES
  ),
  selectEnteringOnOnlineCodePageWithDirectSalesParameter: selectEnteringOnOnlineCodePageWithQueryParam(
    OWCC_QUERY_PARAM.DIRECT_SALES
  )
};

/**
 * 👉 Make sure the app is properly initialized before using snaphots.
 * Query Param snapshots might NOT be available at a very early stage during bootstrapping and might lead to unexpected results.
 */
export const fromRouterAsSnapshot = {
  selectAbTestingQueryParam: selectQueryParamOnce(OWCC_QUERY_PARAM.AB_TESTING), // use with store.pipe()
  selectVehicleIdQueryParam: selectQueryParamOnce(OWCC_QUERY_PARAM.VEHICLE_ID), // use with store.pipe()
  selectOutletIdQueryParam: selectQueryParamOnce(AppRouteQueryParam.GsOutletId), // use with store.pipe()
  selectFcisEnvironmentQueryParam: selectQueryParamOnce(OWCC_QUERY_PARAM.FCIS_ENVIRONMENT) // use with store.pipe()
};

/**
 * Similar to rxjs firstValueFrom() it returns the first value of the given observable. Not as a promise but a real value instead.
 * This means there needs the be a value already in the observable to not get undefined!
 */
export const firstValueFromSync = <T = unknown>(observable: Observable<T>): T | undefined => {
  let firstValueFromObservable: T | undefined = undefined;
  observable.pipe(first()).subscribe(value => {
    firstValueFromObservable = value;
  });
  return firstValueFromObservable;
};

export const fromRouter = {
  selectCurrentRoute,
  selectIsValidRoute,
  selectCarlLabelQueryParam: selectQueryParam(OWCC_QUERY_PARAM.CARL_LABEL),
  selectFormattingTagLabelQueryParam: selectQueryParam(OWCC_QUERY_PARAM.FORMATTING_TAG),
  selectIsOnErrorPage: selectIsOnUrl('/error')
};
